import { For, Show, createSignal, onCleanup, onMount } from "solid-js";

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "~/lib/hooks";
import type { NotificationRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

/**
 * Bell icon in the header. Polls every 60s for the unread count.
 * Dropdown shows the last ~10 notifications; clicking one marks it read
 * and (when link_path is set) navigates to the related entity.
 *
 * Read-on-open is a v1.x choice — for power users who want to triage
 * individually we have explicit per-row "mark read" via the link click,
 * plus "Mark all read" in the dropdown footer.
 */
export function NotificationsBell(props: Props) {
  const list = useNotifications(props.wsKey, () => ({ limit: 10 }));
  const markRead = useMarkNotificationRead(props.wsKey);
  const markAllRead = useMarkAllNotificationsRead(props.wsKey);
  const [open, setOpen] = createSignal(false);
  let rootRef: HTMLDivElement | undefined;

  // Close on outside click / Esc.
  onMount(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef) return;
      if (!rootRef.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    onCleanup(() => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    });
  });

  const unread = () => list.data?.unread_count ?? 0;

  const handleRowClick = (n: NotificationRead) => {
    if (!n.read_at) {
      void markRead.mutateAsync({ id: n.id });
    }
    setOpen(false);
  };

  return (
    <div ref={rootRef} class="relative">
      <button
        type="button"
        class="btn btn-ghost btn-sm btn-circle"
        aria-label={`Notifications (${unread()} unread)`}
        aria-expanded={open()}
        onClick={() => setOpen((o) => !o)}
      >
        <span class="relative inline-flex items-center justify-center" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-5 h-5"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10 21a2 2 0 0 0 4 0" />
          </svg>
          <Show when={unread() > 0}>
            <span class="absolute -top-1 -right-1 badge badge-error badge-xs font-mono">
              {unread() > 99 ? "99+" : unread()}
            </span>
          </Show>
        </span>
      </button>

      <Show when={open()}>
        <div
          class="absolute right-0 mt-2 z-30 w-[min(24rem,90vw)] max-h-[60vh] overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
          role="dialog"
          aria-label="Notifications"
        >
          <div class="flex items-center justify-between px-3 py-2 border-b border-base-200">
            <span class="text-sm font-medium">Notifications</span>
            <Show when={unread() > 0}>
              <button
                type="button"
                class="link link-primary text-xs"
                onClick={() => void markAllRead.mutateAsync()}
              >
                Mark all read
              </button>
            </Show>
          </div>

          <Show when={list.isLoading}>
            <p class="px-3 py-4 text-sm opacity-60">Loading…</p>
          </Show>

          <Show when={!list.isLoading && (list.data?.items.length ?? 0) === 0}>
            <p class="px-3 py-6 text-sm opacity-60 text-center">
              No notifications yet. Trigger a scan from the Review Queue and it'll land here when it
              finishes.
            </p>
          </Show>

          <ul>
            <For each={list.data?.items ?? []}>
              {(n) => <Row n={n} wsKey={props.wsKey} onClick={() => handleRowClick(n)} />}
            </For>
          </ul>
        </div>
      </Show>
    </div>
  );
}

function Row(props: {
  n: NotificationRead;
  wsKey: () => string;
  onClick: () => void;
}) {
  const inner = () => (
    <div class={`px-3 py-2 ${props.n.read_at ? "opacity-60" : "bg-warning/5"}`}>
      <div class="flex items-start gap-2">
        <Show when={!props.n.read_at}>
          <span class="mt-1.5 w-2 h-2 rounded-full bg-warning shrink-0" aria-hidden="true" />
        </Show>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium truncate">{props.n.title}</div>
          <Show when={props.n.body}>
            <p class="text-xs opacity-70 mt-0.5">{props.n.body}</p>
          </Show>
          <p class="text-xs opacity-50 mt-0.5">{relativeTime(props.n.created_at)}</p>
        </div>
      </div>
    </div>
  );

  // We can't render a TanStack Link from a string link_path (typed routes
  // wouldn't accept it), so for v1.x use a plain anchor for path
  // navigation. The router still handles it client-side via SPA history.
  if (props.n.link_path) {
    return (
      <li>
        <a href={props.n.link_path} class="block hover:bg-base-200" onClick={props.onClick}>
          {inner()}
        </a>
      </li>
    );
  }
  return (
    <li>
      <button type="button" class="w-full text-left hover:bg-base-200" onClick={props.onClick}>
        {inner()}
      </button>
    </li>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
